import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, Alert, Space } from 'antd';
import { UserOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import session from '../../utils/session';
import mixpanel from '../../utils/mixpanel';
import resources from '../../reducers/login/resources';
import { checkIfUserIsNew } from '../../reducers/user/resources';
import { initRoom } from '../../io';
import { takePostLoginRedirect } from '../../utils/post-login-redirect';
import config from '../../config';
import constants from '../../constants';
import CountdownButton from '../settings/countdown';
import './AntdLogin.css';

const { Title, Link, Text } = Typography;

const AntdLogin = ({ history, initRoom }) => {
  const [form] = Form.useForm();
  const [tfaForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [show2faForm, setShow2faForm] = useState(false);
  const [requestId, setRequestId] = useState('');
  const [resetTfaTimer, setResetTfaTimer] = useState(false);

  useEffect(() => {
    const storedRequestId = session.get('login_tfa_req_id');
    if (storedRequestId) {
      setRequestId(storedRequestId);
      setShow2faForm(true);
    }
  }, []);

  const processLogin = async () => {
    const user = session.get('tempUser');
    if (!user) return;

    const fromEmailParam = session.getURLParam('fromemail');
    const routeParam = session.getURLParam('route');
    const isAdmin = user.role === config.ghAdminRole;

    session.set('token', user.token);
    session.set('userBots', user.bots);
    session.set('userData', user);
    // Stash family profiles for multi-brand notification session-swapping.
    session.set('allProfiles', user.allProfiles || [user]);

    initRoom(user.id);

    try {
      session.unset('tempUser');
      const res = await checkIfUserIsNew();
      const { new_user: newUser } = res.data;

      if (newUser) {
        mixpanel.track('auth:signin new user');
        return history.replace('/account-setup');
      }

      mixpanel.track('auth:login', { source: "webapp" });

      // Always consume, even when an email link wins below, so a stale
      // destination can't resurface on a later login.
      const stashedRedirect = takePostLoginRedirect();

      if (!isAdmin && fromEmailParam) {
        return history.replace(`/${routeParam}`);
      }

      if (stashedRedirect) {
        return history.replace(stashedRedirect);
      }

      history.replace('/inbox');
    } catch (err) {
      console.error('Login process error:', err);
      setError('An error occurred during login. Please try again.');
    }
  };

  const onFinishLogin = async (values) => {
    setLoading(true);
    setError('');

    try {
      const res = await resources.authenticate({
        username: values.username,
        password: values.password,
        source: 'web',
      });

      const user = res.data;
      session.set('tempUser', user);

      if (user.tfa_option) {
        if (!user.userMobile) {
          setError('User has no mobile number for verification.');
          setLoading(false);
          return;
        }

        const tfaRes = await resources.requestVerification({ userNumber: user.userMobile });
        session.set('login_tfa_req_id', tfaRes.data);
        setRequestId(tfaRes.data);
        setShow2faForm(true);
      } else {
        await processLogin();
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const onFinishVerification = async (values) => {
    setLoading(true);
    setError('');

    try {
      const res = await resources.checkVerification({ requestId, code: values.code });
      if (res.data.status === '0') {
        await processLogin();
        setShow2faForm(false);
        session.unset('login_tfa_req_id');
      } else if (res.data.status === '16') {
        setError(constants.tfa.check.notMatch);
      } else {
        setError(constants.tfa.check.checkFailed);
        setShow2faForm(false);
        session.unset('login_tfa_req_id');
      }
    } catch (err) {
      setError(constants.tfa.check.checkFailed);
      setShow2faForm(false);
      session.unset('login_tfa_req_id');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelVerification = async () => {
    setLoading(true);
    try {
      const res = await resources.cancelVerification({ requestId });
      if (['0', '6', '19'].includes(res.data.status)) {
        setShow2faForm(false);
        session.unset('login_tfa_req_id');
        session.unset('tempUser');
        setError(res.data.status === '6' ? constants.tfa.cancellation.expiredRequest : '');
      } else {
        setError(constants.tfa.cancellation.cancelFailed);
      }
    } catch (err) {
      setError(constants.tfa.cancellation.cancelFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setError('');
    try {
      await resources.cancelVerification({ requestId });
      const tempUser = session.get('tempUser');
      const res = await resources.requestVerification({ userNumber: tempUser.userMobile });
      session.set('login_tfa_req_id', res.data);
      setRequestId(res.data);
      setResetTfaTimer(true);
    } catch (err) {
      setError(constants.tfa.resend.resendFailed);
    } finally {
      setLoading(false);
    }
  };

  const renderLoginForm = () => (
    <Form
      form={form}
      name="login"
      onFinish={onFinishLogin}
      layout="vertical"
      size="large"
      requiredMark={false}
    >
      <div className="login-header">
        <Title level={2} className="login-title">Sign In</Title>
        <Text type="secondary">Welcome back! Please enter your details.</Text>
      </div>

      <Form.Item
        name="username"
        rules={[{ required: true, message: 'Please enter your username' }]}
      >
        <Input 
          prefix={<UserOutlined className="site-form-item-icon" />} 
          placeholder="Username" 
          autoFocus 
        />
      </Form.Item>

      <Form.Item
        name="password"
        rules={[{ required: true, message: 'Please enter your password' }]}
      >
        <Input.Password
          prefix={<LockOutlined className="site-form-item-icon" />}
          placeholder="Password"
        />
      </Form.Item>

      {error && <Alert message={error} type="error" showIcon className="login-alert" />}

      <Form.Item>
        <Button type="primary" htmlType="submit" className="login-form-button" loading={loading} block>
          SIGN IN
        </Button>
      </Form.Item>

      <div className="login-form-extras">
        <Link onClick={() => history.push('/forgot-password')}>Forgot password?</Link>
        <div className="signup-link">
          Don't have an account? <Link  onClick={() => window.location.href = 'https://gohire.com/recruiting-chatbot-demo/'}>Sign Up</Link>
        </div>
      </div>
    </Form>
  );

  const render2faForm = () => (
    <Form
      form={tfaForm}
      name="2fa"
      onFinish={onFinishVerification}
      layout="vertical"
      size="large"
    >
      <div className="login-header">
        <Title level={3}>Two-Step Verification</Title>
        <Text type="secondary">Enter the verification code sent to your mobile number.</Text>
      </div>

      <Form.Item
        name="code"
        rules={[{ required: true, message: 'Please enter the verification code' }]}
      >
        <Input 
          prefix={<SafetyCertificateOutlined className="site-form-item-icon" />} 
          placeholder="Verification code" 
        />
      </Form.Item>

      {error && <Alert message={error} type="error" showIcon className="login-alert" />}

      <Space direction="vertical" style={{ width: '100%' }}>
        <Button type="primary" htmlType="submit" className="login-form-button" loading={loading} block>
          VERIFY
        </Button>
        <Button onClick={handleCancelVerification} block>
          CANCEL
        </Button>
        {requestId && (
          <div className="tfa-countdown">
            <CountdownButton
              handleResend={handleResendVerification}
              resetTfaTimer={resetTfaTimer}
              handleReset={() => setResetTfaTimer(false)}
            />
          </div>
        )}
      </Space>
    </Form>
  );

  return (
    <div className="antd-login-card">
      <div className="login-logo-container">
        <img src="/assets/images/logo.png" alt="GoHire" className="login-card-logo" />
      </div>
      {show2faForm ? render2faForm() : renderLoginForm()}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  initRoom(id) {
    return initRoom(id);
  },
});

export default connect(null, mapDispatchToProps)(withRouter(AntdLogin));
